import type { Beneficiary } from '@/api/generated/model';
import { AllocationPercentEditor } from '@/components/planning/AllocationPercentEditor';
import { DualPercentSplitSlider } from '@/components/planning/DualPercentSplitSlider';
import {
  StackedAllocationBar,
  type AllocationSegment,
} from '@/components/planning/StackedAllocationBar';
import { useLanguage } from '@/i18n/language-hooks';
import { formatCurrency } from '@/lib/utils';

export interface SuccessionEstateAllocationEditorProps {
  scopeTotal: number;
  currency: string;
  beneficiaries: Beneficiary[];
  percents: number[];
  onPercentsChange: (percents: number[]) => void;
  segments?: AllocationSegment[];
  selectedBeneficiaryId?: string | null;
  onSelectBeneficiary?: (id: string | null) => void;
  onRemoveBeneficiary?: (id: string) => void;
  showBarSelection?: boolean;
}

export function SuccessionEstateAllocationEditor({
  scopeTotal,
  currency,
  beneficiaries,
  percents,
  onPercentsChange,
  segments,
  selectedBeneficiaryId,
  onSelectBeneficiary,
  onRemoveBeneficiary,
  showBarSelection = true,
}: SuccessionEstateAllocationEditorProps) {
  const { t } = useLanguage();
  const count = beneficiaries.length;
  const removeLabel = t('common.delete');

  if (count === 0) {
    return (
      <div
        className='bg-fin-gray-800 h-3 w-full overflow-hidden rounded-full'
        role='img'
        aria-label={t('planning.succession.simpleAllocation')}
      />
    );
  }

  const entries = beneficiaries.map((beneficiary, index) => ({
    id: beneficiary.id,
    label: beneficiary.name || t('planning.succession.unnamed'),
    percent: percents[index] ?? beneficiary.allocationPercent,
  }));

  const barSegments: AllocationSegment[] =
    segments ??
    entries.map((entry) => ({
      id: entry.id,
      label: entry.label,
      percent: entry.percent,
      valueLabel: formatCurrency(scopeTotal * (entry.percent / 100), currency),
    }));

  const selectHandler =
    showBarSelection && onSelectBeneficiary
      ? (id: string) =>
          onSelectBeneficiary(selectedBeneficiaryId === id || id === 'unallocated' ? null : id)
      : undefined;

  if (count === 1) {
    return (
      <StackedAllocationBar
        segments={barSegments}
        selectedId={showBarSelection ? selectedBeneficiaryId : undefined}
        onSelect={selectHandler}
        onRemove={onRemoveBeneficiary}
        removeLabel={removeLabel}
        ariaLabel={t('planning.succession.simpleAllocation')}
      />
    );
  }

  if (count === 2) {
    const [first, second] = entries;
    return (
      <DualPercentSplitSlider
        leftId={first.id}
        rightId={second.id}
        leftLabel={first.label}
        rightLabel={second.label}
        leftPercent={first.percent}
        leftValueLabel={formatCurrency(scopeTotal * (first.percent / 100), currency)}
        rightValueLabel={formatCurrency(scopeTotal * (second.percent / 100), currency)}
        onChange={(left, right) => onPercentsChange([left, right])}
        onRemove={onRemoveBeneficiary}
        removeLabel={removeLabel}
      />
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <StackedAllocationBar
        segments={barSegments}
        selectedId={showBarSelection ? selectedBeneficiaryId : undefined}
        onSelect={selectHandler}
        onRemove={onRemoveBeneficiary}
        removeLabel={removeLabel}
        ariaLabel={t('planning.succession.simpleAllocation')}
      />
      <AllocationPercentEditor entries={entries} onBulkChange={onPercentsChange} />
    </div>
  );
}
